import { useState, useMemo, Fragment } from 'react'
import { FileText, TrendingUp, Wallet, DollarSign, ChevronRight, ChevronDown } from 'lucide-react'
import { Slider } from '@/components/ui/slider'
import {
  Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Line, ComposedChart, ResponsiveContainer, LabelList,
} from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useDashReport } from '@/modules/dash/hooks/useDashReport'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'

// ── Tipos ──────────────────────────────────────────────────────────────────

interface FunnelMes1 {
  registros_mes1_por_rep: number
  tasa_participacion_mes1: number
  activos_mes1_por_rep: number
  facturas_mes1_por_rep: number
  fact_por_dep_activo: number
  puntos_por_factura: number
  puntos_mes1_por_rep: number
  potencial_sol_por_rep: number
  tasa_canje_mensual: number
  canje_sol_por_rep: number
  canje_usd_por_rep: number
  n_registros_mes1: number
  n_activos_mes1: number
  n_facturas_mes1: number
  n_puntos_mes1: number
  n_potencial_sol: number
  n_canje_sol: number
  n_canje_usd: number
}

interface CurvaPoint {
  mes: string
  registros: number
  acumulado: number
}

interface ProyeccionData {
  representantes_base: number
  promedios: {
    farmacias_por_rep: number
    registros_mes1_por_rep: number
    facturas_mes1_por_rep: number
    canje_ref_usd_por_rep: number
  }
  proyeccion: {
    dependientes_totales: number
    registros_mes1: number
    facturas_mes1: number
    canje_sol_mes1: number
  }
  curva_registros: CurvaPoint[]
  funnel_mes1: FunnelMes1
  nota_metodologia: string
}

// ── Constantes ──────────────────────────────────────────────────────────────

const TEAL = '#0d9488'        // teal-600
const TEAL_LIGHT = '#0d948818' // teal-600 ~9% alpha

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmt(n: number): string {
  return n.toLocaleString('es-PE')
}

// Permite que los inputs numéricos queden vacíos mientras se editan
const num = (x: number | '') => (typeof x === 'number' ? x : 0)

// ── Cálculo de proyección ──────────────────────────────────────────────────────

interface ProyeccionCalc {
  activos: number
  facturas: number
  curva: CurvaPoint[]
}

function calcularProyeccion(D: number, part: number, f: FunnelMes1, p: ProyeccionData['promedios']): ProyeccionCalc {
  const activos = Math.round(D * part)
  const facturas = Math.round(activos * f.fact_por_dep_activo)

  // Curva ramp-up: distribuye los D dependientes en 5 meses (patrón Medifarma)
  const mes1Share = p.registros_mes1_por_rep / p.farmacias_por_rep
  const mes1 = Math.round(D * mes1Share)
  const resto = D - mes1
  const dist = [296, 149, 173, 75]
  const distTotal = dist.reduce((a, b) => a + b, 0)
  let acum = mes1
  const curva: CurvaPoint[] = [
    { mes: 'Mes 1', registros: mes1, acumulado: mes1 },
    ...dist.map((d, i) => {
      const reg = Math.round(resto * d / distTotal)
      acum += reg
      return { mes: `Mes ${i + 2}`, registros: reg, acumulado: acum }
    }),
  ]

  return { activos, facturas, curva }
}

// ── KPI Card ─────────────────────────────────────────────────────────────────

function KpiCard({
  label, value, sub, icon: Icon,
}: {
  label: string; value: string; sub?: string; icon: React.ElementType
}) {
  return (
    <div
      className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-card h-full"
      style={{ backgroundImage: `linear-gradient(to right, ${TEAL_LIGHT}, transparent 60%)` }}
    >
      <div className="h-full px-4 py-4 flex flex-col justify-center gap-2">
        <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">{label}</p>
        <div className="flex items-center gap-3">
          <div className="shrink-0">
            <Icon className="h-9 w-9" style={{ color: TEAL }} />
          </div>
          <div className="flex-1 min-w-0 text-right">
            <div className="text-3xl font-bold text-foreground leading-tight">{value}</div>
            {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Slider de parámetro ─────────────────────────────────────────────────────────

function SliderInput({ label, value, min, max, step, onChange, prefix, suffix, help }: {
  label: string
  value: number | ''
  min: number
  max: number
  step: number
  onChange: (v: number | '') => void
  prefix?: string
  suffix?: string
  help?: string
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2 gap-2">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
        <div className="flex items-center gap-1">
          {prefix && <span className="text-sm text-muted-foreground">{prefix}</span>}
          <input
            type="number"
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
            className="w-20 text-right text-lg font-bold bg-transparent border-b border-transparent hover:border-gray-300 focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            style={{ color: TEAL }}
          />
          {suffix && <span className="text-sm text-muted-foreground">{suffix}</span>}
        </div>
      </div>
      <Slider
        value={[num(value)]}
        min={min}
        max={max}
        step={step}
        onValueChange={(v) => onChange(v[0])}
        className="[&>span:first-child]:bg-teal-600/20 [&>span:first-child>span]:bg-teal-600 [&_[role=slider]]:border-teal-600"
      />
      {help && <p className="text-[11px] text-muted-foreground mt-1.5">{help}</p>}
    </div>
  )
}

// ── Tooltip custom ────────────────────────────────────────────────────────────

function CurvaTooltip({ active, payload, label }: {
  active?: boolean; payload?: { value: number; name: string }[]; label?: string
}) {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-white dark:bg-card border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 shadow text-sm">
      <p className="font-semibold text-foreground mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.name} className="text-muted-foreground">
          {p.name === 'registros' ? 'Nuevos' : 'Acumulado'}: <span className="font-medium text-foreground">{fmt(p.value)}</span>
        </p>
      ))}
    </div>
  )
}

// ── Página principal ─────────────────────────────────────────────────────────

export function TonalFidelizacionProyeccionPage() {
  useDocumentTitle('Proyección · Programa de Fidelización')

  const { data, isLoading, isError } = useDashReport<ProyeccionData>('medifarma-peru/proyeccion')
  const [dependientes, setDependientes] = useState<number | ''>(1000)
  const [participacion, setParticipacion] = useState<number | ''>(32)
  const [moneda, setMoneda] = useState<'PEN' | 'USD'>('PEN')
  const [valorFacturaPen, setValorFacturaPen] = useState<number | ''>(19.89)
  const [valorFacturaUsd, setValorFacturaUsd] = useState<number | ''>(8.41)
  const [canjePct, setCanjePct] = useState<number | ''>(5)

  const calc = useMemo(() => {
    if (!data) return null
    return calcularProyeccion(num(dependientes), num(participacion) / 100, data.funnel_mes1, data.promedios)
  }, [data, dependientes, participacion])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: TEAL }} />
      </div>
    )
  }

  if (isError || !data || !calc) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center text-gray-500">
        Error al cargar los datos.
      </div>
    )
  }

  const f = data.funnel_mes1
  const { activos, facturas, curva } = calc
  const cur = moneda === 'USD' ? '$' : 'S/'
  const curFmt = (n: number) => `${cur} ${fmt(n)}`
  // Ventas: cada país tiene su ticket promedio propio (no es conversión)
  const valorFactura = moneda === 'USD' ? num(valorFacturaUsd) : num(valorFacturaPen)
  const ventas = Math.round(facturas * valorFactura)
  // Costo de canje ≈ % de las ventas totales (mismo mercado/moneda)
  const canje = Math.round(ventas * num(canjePct) / 100)

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-6 space-y-6">

      {/* ── Header ── */}
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardContent className="py-4 px-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-5">
              <img src="/logo-tonal-negro.png" alt="Tonal" className="h-12 w-auto dark:invert" />
              <div className="border-l border-gray-200 dark:border-gray-700 pl-5">
                <h1 className="text-xl font-bold text-foreground leading-tight">
                  Proyección — Programa de Fidelización
                </h1>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Estimación basada en benchmarks de programas de fidelización
                </p>
              </div>
            </div>
            <div className="inline-flex shrink-0 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden text-sm">
              <button
                type="button"
                onClick={() => setMoneda('PEN')}
                className={`px-3 py-1.5 font-medium transition-colors ${moneda === 'PEN' ? 'text-white' : 'text-muted-foreground bg-white dark:bg-card hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                style={moneda === 'PEN' ? { backgroundColor: TEAL } : undefined}
              >
                Perú · S/
              </button>
              <button
                type="button"
                onClick={() => setMoneda('USD')}
                className={`px-3 py-1.5 font-medium transition-colors ${moneda === 'USD' ? 'text-white' : 'text-muted-foreground bg-white dark:bg-card hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                style={moneda === 'USD' ? { backgroundColor: TEAL } : undefined}
              >
                Ecuador · $
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Resumen ejecutivo ── */}
      <Card className="border border-gray-200 dark:border-gray-700 bg-white dark:bg-card">
        <CardContent className="py-4 px-6">
          <p className="text-sm text-muted-foreground leading-relaxed">
            De <span className="font-semibold text-foreground">{fmt(num(dependientes))} dependientes registrados</span>, con una
            participación del <span className="font-semibold text-foreground">{participacion}%</span> se esperan{' '}
            <span className="font-semibold text-foreground">~{fmt(activos)} dependientes activos</span>, que subirán{' '}
            <span className="font-semibold text-foreground">~{fmt(facturas)} facturas</span>, equivalentes a{' '}
            <span className="font-semibold text-foreground">{curFmt(ventas)}</span> en ventas proyectadas{' '}
            <span className="text-xs">(ticket prom. {cur} {valorFactura})</span>. El costo de canjes estimado es{' '}
            <span className="font-semibold text-foreground">{curFmt(canje)}</span>{' '}
            <span className="text-xs">(~{canjePct}% de las ventas)</span>.
          </p>
        </CardContent>
      </Card>

      {/* ── KPIs (100% ancho) ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              icon={DollarSign}
              label="Ventas proyectadas — mes 1"
              value={curFmt(ventas)}
              sub={`${fmt(facturas)} facturas × ${cur} ${valorFactura} ticket`}
            />
            <KpiCard
              icon={TrendingUp}
              label="Dependientes activos"
              value={fmt(activos)}
              sub={`${participacion}% de ${fmt(num(dependientes))} registrados`}
            />
            <KpiCard
              icon={FileText}
              label="Facturas — mes 1"
              value={fmt(facturas)}
              sub={`~${f.fact_por_dep_activo} facturas por dep. activo`}
            />
            <KpiCard
              icon={Wallet}
              label="Costo canjes estimado"
              value={curFmt(canje)}
              sub={`~${canjePct}% de las ventas`}
            />
      </div>

      {/* ── Gráfica (70%) + ajuste de proyección (30%) ── */}
      <div className="grid grid-cols-1 lg:grid-cols-10 gap-6">
        <div className="lg:col-span-7">
          <Card className="border border-gray-200 dark:border-gray-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Ramp-up de dependientes registrados — {fmt(num(dependientes))} dependientes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={curva} margin={{ top: 16, right: 24, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="bar" tick={{ fontSize: 11 }} width={55} tickFormatter={(v) => fmt(v)} />
                  <YAxis yAxisId="line" orientation="right" tick={{ fontSize: 11 }} width={60} tickFormatter={(v) => fmt(v)} />
                  <Tooltip content={<CurvaTooltip />} />
                  <Bar yAxisId="bar" dataKey="registros" name="registros" fill={TEAL} radius={[4, 4, 0, 0]} opacity={0.85}>
                    <LabelList dataKey="registros" position="top" formatter={(v: number) => fmt(v)} style={{ fontSize: 11, fill: '#6b7280' }} />
                  </Bar>
                  <Line yAxisId="line" dataKey="acumulado" name="acumulado" stroke="#2fbc80" strokeWidth={2} dot={{ r: 4, fill: '#2fbc80' }} />
                </ComposedChart>
              </ResponsiveContainer>
              <div className="flex items-center gap-6 mt-2 justify-center">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: TEAL }} />
                  Registros nuevos por mes
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <div className="w-4 h-0.5 bg-[#2fbc80]" />
                  Acumulado
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Ajuste de proyección (30%) */}
        <div className="lg:col-span-3">
          <Card className="border border-gray-200 dark:border-gray-700 h-full flex flex-col">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Ajustar la proyección
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col justify-center gap-6">
            <SliderInput
              label="Dependientes registrados"
              value={dependientes}
              min={0}
              max={3000}
              step={10}
              onChange={setDependientes}
            />
            <SliderInput
              label="Participación"
              value={participacion}
              min={0}
              max={100}
              step={1}
              onChange={setParticipacion}
              suffix="%"
              help={`benchmark real: ${Math.round(f.tasa_participacion_mes1 * 100)}%`}
            />
            <SliderInput
              label="Valor promedio por factura"
              value={moneda === 'USD' ? valorFacturaUsd : valorFacturaPen}
              min={0}
              max={moneda === 'USD' ? 30 : 60}
              step={0.1}
              onChange={(v) => (moneda === 'USD' ? setValorFacturaUsd(v) : setValorFacturaPen(v))}
              prefix={cur}
            />
            <SliderInput
              label="Costo de canje"
              value={canjePct}
              min={0}
              max={30}
              step={0.5}
              onChange={setCanjePct}
              suffix="%"
            />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ── Cómo se construye la proyección (funnel horizontal) ── */}
      <Card className="border border-gray-200 dark:border-gray-700">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Cómo se construye la proyección
            </CardTitle>
            <span className="text-xs text-muted-foreground/70 italic">Los canjes se materializan a partir del mes 2</span>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col lg:flex-row lg:items-stretch gap-2">
            {[
              { label: 'Dependientes registrados', primary: fmt(num(dependientes)), sub: 'dependientes' },
              { label: 'Participan', primary: fmt(activos), sub: `${participacion}% suben ≥1 factura` },
              { label: 'Facturas — mes 1', primary: fmt(facturas), sub: `~${f.fact_por_dep_activo} por dep. activo` },
              { label: 'Ventas estimadas', primary: curFmt(ventas), sub: `${cur} ${valorFactura} por factura` },
              { label: 'Costo de canjes', primary: curFmt(canje), sub: `~${canjePct}% de las ventas` },
            ].map((s, i, arr) => (
              <Fragment key={i}>
                <div
                  className="flex-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-card p-4"
                  style={{ backgroundImage: `linear-gradient(135deg, ${TEAL_LIGHT}, transparent 60%)` }}
                >
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold mb-2" style={{ backgroundColor: TEAL }}>
                    {i + 1}
                  </div>
                  <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">{s.label}</p>
                  <p className="text-xl font-bold text-foreground mt-0.5">{s.primary}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{s.sub}</p>
                </div>
                {i < arr.length - 1 && (
                  <div className="flex items-center justify-center text-gray-300 dark:text-gray-600 shrink-0">
                    <ChevronRight className="h-5 w-5 hidden lg:block" />
                    <ChevronDown className="h-5 w-5 lg:hidden" />
                  </div>
                )}
              </Fragment>
            ))}
          </div>        </CardContent>
      </Card>

      {/* Footer */}
      <p className="text-center text-xs text-muted-foreground/40 pb-2">
        Tonal Strategies · Programa de fidelización
      </p>
    </div>
  )
}
